import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { Exact } from "@Helpers/types/exact";
import type { BcfIssueItem } from "@ServerTypes/issues/issues.schema";

/** BCF file buffer data (S3 response body structure, client-side only) */
export interface BcfBufferData {
  Body?: {
    data?: number[];
    type?: string;
  };
  ContentType?: string;
  ContentLength?: number;
  ETag?: string;
}

export interface BcfState {
  currentBcfId: string;
  bcfData: BcfBufferData | null;
  isAutoBcfLoaded: boolean;
}

const initialBcfState: BcfState = {
  currentBcfId: "",
  bcfData: null,
  isAutoBcfLoaded: false,
};

const itemsHaveErrorSlice = createSlice({
  name: "itemsHaveError",
  initialState: false,
  reducers: {
    setItemsHaveError: (_state: boolean, action: PayloadAction<boolean>) => action.payload,
  },
});

const itemsAreLoadingSlice = createSlice({
  name: "itemsAreLoading",
  initialState: false,
  reducers: {
    setItemsAreLoading: (_state: boolean, action: PayloadAction<boolean>) => action.payload,
  },
});

const itemsSlice = createSlice({
  name: "items",
  initialState: [] as BcfIssueItem[],
  reducers: {
    setItems: (_state: BcfIssueItem[], action: PayloadAction<BcfIssueItem[]>) => action.payload,
  },
});

const bcfDataSlice = createSlice({
  name: "bcfData",
  initialState: initialBcfState,
  reducers: {
    saveBcfData: (state: BcfState, action: PayloadAction<string>) => {
      if (action.payload) {
        state.currentBcfId = action.payload;
      }
    },
    setBcfBufferData: (state: BcfState, action: PayloadAction<BcfBufferData | null>) => {
      if (action.payload) {
        state.bcfData = action.payload;
      }
    },
    setAutoBcfLoaded: (state: BcfState, action: PayloadAction<boolean>) => {
      state.isAutoBcfLoaded = action.payload;
    },
  },
});

export const { setItemsHaveError } = itemsHaveErrorSlice.actions;
export const { setItemsAreLoading } = itemsAreLoadingSlice.actions;
export const { setItems } = itemsSlice.actions;

const { saveBcfData, setBcfBufferData: _setBcfBufferDataRaw, setAutoBcfLoaded } = bcfDataSlice.actions;

/**
 * Exact<>-wrapped: object payload (or null), catches excess properties via variables.
 *
 * `Exact<>` is applied to the PARAMETER (`T & Exact<…>`) rather than used as the constraint — the
 * same form `contracts.reducer.ts` documents. Under `exactOptionalPropertyTypes` type-fest's
 * `ExactObject` homomorphic mapping re-emits every optional member as `k?: V | undefined`, so a
 * bare `T extends Exact<BcfBufferData | null, T>` no longer proves `T` is a `BcfBufferData | null`.
 */
export const setBcfBufferData = <T extends BcfBufferData | null>(payload: T & Exact<BcfBufferData | null, T>) =>
  _setBcfBufferDataRaw(payload);
export { saveBcfData, setAutoBcfLoaded };

export const itemsHaveError = itemsHaveErrorSlice.reducer;
export const itemsAreLoading = itemsAreLoadingSlice.reducer;
export const items = itemsSlice.reducer;
export const bcfData = bcfDataSlice.reducer;
